import { useTranslation } from 'react-i18next'
import {
  COMPANY_FUNCTIONS,
  COMPANY_ONLY_FIELDS,
  COMPANY_PROFILE_FIELDS,
  COMPANY_PROFILE_NUMERIC_FIELDS,
  MAX_SITES,
  parseFunctions,
  REVENUE_RANGES,
  STARTUP_ONLY_FIELDS,
  WORKSPACE_KINDS,
  type Workspace,
} from 'shared'
import { Field, Input, Select, Textarea } from '../ui'

const KIND_ONLY: readonly string[] = [...STARTUP_ONLY_FIELDS, ...COMPANY_ONLY_FIELDS]
/** Text fields every company has, whatever its kind; the others are picked by `kind`. */
const SHARED_TEXT_FIELDS = COMPANY_PROFILE_FIELDS.filter(
  (field) => field !== 'kind' && !KIND_ONLY.includes(field)
)
/** Startup columns written as free text; TRL is a number and drawn apart. */
const STARTUP_TEXT_FIELDS = STARTUP_ONLY_FIELDS.filter((field) => field !== 'trl')

/** Every profile column plus the workspace's own fields, held as form strings. */
const FORM_FIELDS = ['name', 'description', ...COMPANY_PROFILE_FIELDS, ...COMPANY_PROFILE_NUMERIC_FIELDS] as const

export type WorkspaceProfileValues = Record<(typeof FORM_FIELDS)[number], string>

export const EMPTY_PROFILE: WorkspaceProfileValues = {
  ...(Object.fromEntries(FORM_FIELDS.map((field) => [field, ''])) as WorkspaceProfileValues),
  kind: 'startup',
}

/** Numeric columns must reach the API as numbers or null, never as empty strings. */
export function toApiPayload(values: WorkspaceProfileValues) {
  const payload: Record<string, string | number | null> = {}
  // The other kind's fields are not sent at all, so they stay as saved on the server.
  const hidden: readonly string[] =
    values.kind === 'company' ? STARTUP_ONLY_FIELDS : COMPANY_ONLY_FIELDS

  for (const field of FORM_FIELDS) {
    if (hidden.includes(field)) continue
    payload[field] = values[field]
  }
  for (const field of COMPANY_PROFILE_NUMERIC_FIELDS) {
    if (hidden.includes(field)) continue
    payload[field] = values[field] ? Number(values[field]) : null
  }
  // `kind` is never empty.
  payload.kind = values.kind || 'startup'

  return payload
}

export function fromWorkspace(workspace: Workspace): WorkspaceProfileValues {
  const values = { ...EMPTY_PROFILE }

  for (const field of FORM_FIELDS) {
    const value = workspace[field as keyof Workspace]
    values[field] = value === null || value === undefined ? '' : String(value)
  }
  values.kind = workspace.kind ?? 'startup'

  return values
}

interface WorkspaceProfileFormProps {
  values: WorkspaceProfileValues
  onChange: (values: WorkspaceProfileValues) => void
  disabled?: boolean
}

export default function WorkspaceProfileForm({
  values,
  onChange,
  disabled,
}: WorkspaceProfileFormProps) {
  const { t } = useTranslation()

  const set = (field: keyof WorkspaceProfileValues, value: string) =>
    onChange({ ...values, [field]: value })

  const isCompany = values.kind === 'company'
  const selectedFunctions = parseFunctions(values.functions_involved)

  const toggleFunction = (code: string) => {
    const next = selectedFunctions.includes(code)
      ? selectedFunctions.filter((item) => item !== code)
      : [...selectedFunctions, code]
    // Keep the canonical order so the stored list does not depend on click order.
    set('functions_involved', COMPANY_FUNCTIONS.filter((item) => next.includes(item)).join(','))
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Field label={t('workspaces.fields.name')} required>
          <Input
            required
            disabled={disabled}
            value={values.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder={t('workspaces.fields.namePlaceholder')}
          />
        </Field>

        <Field label={t('workspaces.fields.description')}>
          <Textarea
            rows={3}
            disabled={disabled}
            value={values.description}
            onChange={(event) => set('description', event.target.value)}
          />
        </Field>
      </div>

      <fieldset className="border-t border-gray-200 pt-6 dark:border-gray-800">
        <legend className="sr-only">{t('workspaces.companyProfile')}</legend>
        <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t('workspaces.companyProfile')}
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('workspaces.fields.kind')}
            hint={t('workspaces.kindHint')}
            className="sm:col-span-2"
          >
            <Select
              disabled={disabled}
              value={values.kind}
              onChange={(event) => set('kind', event.target.value)}
              options={WORKSPACE_KINDS.map((kind) => ({
                value: kind,
                label: t(`workspaces.kinds.${kind}`),
              }))}
            />
          </Field>

          {SHARED_TEXT_FIELDS.map((field) => (
            <Field key={field} label={t(`workspaces.fields.${field}`)}>
              <Input
                disabled={disabled}
                value={values[field]}
                onChange={(event) => set(field, event.target.value)}
              />
            </Field>
          ))}

          <Field label={t('workspaces.fields.founding_year')}>
            <Input
              type="number"
              min={1900}
              max={new Date().getFullYear()}
              disabled={disabled}
              value={values.founding_year}
              onChange={(event) => set('founding_year', event.target.value)}
            />
          </Field>

          {isCompany ? (
            <>
              <Field label={t('workspaces.fields.revenue_range')}>
                <Select
                  disabled={disabled}
                  value={values.revenue_range}
                  onChange={(event) => set('revenue_range', event.target.value)}
                  options={[
                    { value: '', label: '' },
                    ...REVENUE_RANGES.map((range) => ({
                      value: range,
                      label: t(`workspaces.revenueRanges.${range}`),
                    })),
                  ]}
                />
              </Field>

              <Field label={t('workspaces.fields.sites_count')}>
                <Input
                  type="number"
                  min={1}
                  max={MAX_SITES}
                  disabled={disabled}
                  value={values.sites_count}
                  onChange={(event) => set('sites_count', event.target.value)}
                />
              </Field>
            </>
          ) : (
            <>
              {STARTUP_TEXT_FIELDS.map((field) => (
                <Field key={field} label={t(`workspaces.fields.${field}`)}>
                  <Input
                    disabled={disabled}
                    value={values[field]}
                    onChange={(event) => set(field, event.target.value)}
                  />
                </Field>
              ))}

              <Field label={t('workspaces.fields.trl')}>
                <Input
                  type="number"
                  min={1}
                  max={9}
                  disabled={disabled}
                  value={values.trl}
                  onChange={(event) => set('trl', event.target.value)}
                />
              </Field>
            </>
          )}
        </div>

        {isCompany && (
          <fieldset className="mt-4" disabled={disabled}>
            <legend className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('workspaces.fields.functions_involved')}
            </legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {COMPANY_FUNCTIONS.map((code) => (
                <label
                  key={code}
                  className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  <input
                    type="checkbox"
                    checked={selectedFunctions.includes(code)}
                    onChange={() => toggleFunction(code)}
                  />
                  {t(`workspaces.functions.${code}`)}
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </fieldset>
    </div>
  )
}
